/**
 * LEAD PII EXTRACTION
 *
 * Extrait la PII du lead (pilote) depuis les données d'une soumission de
 * formulaire public, pour enrichir le postMessage `DESERTOURS_LEAD` envoyé au
 * site parent (4LTROPHY). Le parent utilise cette PII pour l'advanced matching
 * (Meta CAPI + GA4 MP), et la hashe en SHA-256 côté serveur.
 *
 * Le CRM n'effectue AUCUN hash ni appel réseau tracking : on renvoie les valeurs
 * en clair, trimmées, ou `undefined` si absentes/vides.
 *
 * Pour un formulaire duo (pilote + copilote), on ne prend que la PII du pilote :
 * un field est retenu s'il cible `target_role === 'pilot'` OU s'il est commun
 * (`target_role == null`). Les champs du copilote sont ignorés.
 */
import type { FormFieldWithValues } from '@/types/form';

/** PII du lead en clair, tous les champs optionnels. */
export interface LeadPii {
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  country?: string;
}

/** Mapping contact_field_map canonique → clé de sortie LeadPii. */
const FIELD_MAP: Record<string, keyof LeadPii> = {
  email: 'email',
  phone: 'phone',
  first_name: 'firstName',
  last_name: 'lastName',
  country: 'country',
};

/**
 * Retourne la valeur trimmée non vide de `data[field.id]`, ou `undefined`.
 */
function readTrimmed(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined;
  const trimmed = String(value).trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Extrait la PII du pilote depuis les données de soumission.
 *
 * @param data - Valeurs de la soumission, clées par `field.id`.
 * @param fields - Champs du formulaire avec `contact_field_map` et `target_role`.
 * @returns Un objet LeadPii ne contenant que les champs présents et non vides.
 */
export function extractLeadPii(
  data: Record<string, unknown>,
  fields: FormFieldWithValues[]
): LeadPii {
  const result: LeadPii = {};

  for (const field of fields) {
    const mapping = field.contact_field_map;
    if (!mapping) continue;

    const outputKey = FIELD_MAP[mapping];
    if (!outputKey) continue;

    // Pilote : champ ciblant explicitement le pilote OU champ commun (null/undefined).
    const isPilotOrCommon =
      field.target_role === 'pilot' ||
      field.target_role === null ||
      field.target_role === undefined;
    if (!isPilotOrCommon) continue;

    // Ne pas écraser une valeur déjà trouvée (le pilote explicite est prioritaire
    // sur un éventuel champ commun de même mapping, et le premier champ trouvé gagne).
    if (result[outputKey] !== undefined) continue;

    const value = readTrimmed(data[field.id]);
    if (value !== undefined) {
      result[outputKey] = value;
    }
  }

  return result;
}
